import { Link } from "@ui/Atoms/Link/Link";
import { IconChevronRight } from "@ui/Icons/IconChevronRight";
import type * as React from "react";
import { useState } from "react";

export type PlaceListRowData = {
	id: string;
	name: string;
	href: string;
	/** "Haagse Hout, Den Haag" or just the district. */
	area?: string;
	/** "11 min lopen". */
	distance?: string;
	/** Extra muted line, e.g. "Reel van 2 dagen geleden - 84K views". */
	meta?: string;
	cover?: string | null;
	videoUrl?: string | null;
};

type Props = PlaceListRowData & {
	emptyLabel: string;
	size?: "sm" | "md";
	/** Badges row under the meta (md only). */
	badges?: React.ReactNode;
	/** Trailing element; defaults to a chevron on `sm`. */
	trailing?: React.ReactNode;
};

const THUMB = {
	sm: { w: 48, h: 48 },
	md: { w: 76, h: 96 },
};

function Thumb({
	cover,
	videoUrl,
	name,
	emptyLabel,
	size,
}: Pick<Props, "cover" | "videoUrl" | "name" | "emptyLabel" | "size">) {
	const [failed, setFailed] = useState(false);
	const dims = THUMB[size ?? "md"];

	return (
		<span
			className="relative flex shrink-0 items-center justify-center overflow-hidden rounded-[var(--radius-sm)] border border-[var(--border-strong)] bg-[var(--surface-subtle)]"
			style={{ width: dims.w, height: dims.h }}
		>
			{videoUrl ? (
				<video
					autoPlay
					loop
					muted
					playsInline
					preload="metadata"
					aria-label={name}
					className="absolute inset-0 h-full w-full object-cover"
				>
					<source src={videoUrl} />
				</video>
			) : cover && cover.trim().length > 0 && !failed ? (
				<img
					src={cover}
					alt={name}
					onError={() => setFailed(true)}
					className="absolute inset-0 h-full w-full object-cover"
				/>
			) : (
				<span className="px-1 text-center text-[10px] font-medium text-[var(--content-faint)]">
					{emptyLabel}
				</span>
			)}
		</span>
	);
}

export function PlaceListRow({
	name,
	href,
	area,
	distance,
	meta,
	cover,
	videoUrl,
	emptyLabel,
	size = "md",
	badges,
	trailing,
}: Props) {
	const sub = [area, distance].filter(Boolean).join(" · ");
	const showChevron = size === "sm" && trailing === undefined;

	return (
		<Link
			href={href}
			className="flex items-center gap-3.5 border-t border-[var(--border-subtle)] py-3.5 first:border-t-0"
		>
			<Thumb
				cover={cover}
				videoUrl={videoUrl}
				name={name}
				emptyLabel={emptyLabel}
				size={size}
			/>
			<div className="flex min-w-0 flex-1 flex-col gap-1">
				<div
					className="truncate font-bold text-[var(--content-primary)]"
					style={{ fontSize: size === "sm" ? 14.5 : 15.5 }}
				>
					{name}
				</div>
				{sub && (
					<div className="truncate text-[13px] text-[var(--content-muted)]">
						{sub}
					</div>
				)}
				{size === "md" && meta && (
					<div className="text-[13px] text-[var(--content-faint)]">{meta}</div>
				)}
				{size === "md" && badges && (
					<div className="mt-1 flex flex-wrap gap-1.5">{badges}</div>
				)}
			</div>
			{trailing}
			{showChevron && (
				<IconChevronRight size={16} color="var(--content-faint)" />
			)}
		</Link>
	);
}
